import React from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { AccordianComponent } from '../../../components/accordian/Accordian.components'
import SingleSelect from '../../../components/singleSelect/SingleSelect.component'
import {
  IonButton,
  IonButtons,
  IonCol,
  IonGrid,
  IonIcon,
  IonItem,
  IonItemSliding,
  IonLabel,
  IonRow,
} from '@ionic/react'
import SInput from '../../../components/stringInput/StringInput.component'
import Validation from '../../../components/validation/Validation.component'
import { getImmunizationOptionsSelector } from '../../options/selectors/immunizationOptions.selector'
import { getPatientImmunizationsSelector } from '../selectors'
import {
  addPatientImmunization,
  removePatientImmunization,
  saveImmunization,
  setPatientImmunizationIncompleteReason,
  setPatientImmunizationStatus,
  setPatientImmunizationType,
  setYear,
} from '../store/patientImmunizations.actions'
import { addCircleOutline, trashOutline } from 'ionicons/icons'
import { IPatientImmunization } from '../store/patientImmunizations.types'
import { IOption } from '../../../common/common.types'
import { compareObjectsIfEqual, filterOutObjectKeys } from '../../../utilities/object.util'
import { initialPatientImmunization } from '../store/patientImmunizations.reducers'
import { PatientImmunizationsConstants } from '../store/patientImmunizations.constants'
import './Immunization.content.css'
import { getDateTime } from '../../../utilities'

const Immunization: React.FC = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()

  const immunizationOptions = useSelector(getImmunizationOptionsSelector)
  const immunizationState = useSelector(getPatientImmunizationsSelector)
  const { immunizationStatusOptions, immunizationTypeOptions, immunizationYearOptions } = immunizationOptions
  const { immunizationStatus, immunizationIncompleteReason, immunizations } = immunizationState

  const setImmunizationStatus = (option: IOption) => {
    dispatch(setPatientImmunizationStatus(option, option !== null ? getDateTime() : null))
    if (
      !option ||
      (option && option?.id !== -2 && immunizationIncompleteReason && immunizationIncompleteReason.length > 0)
    ) {
      dispatch(setPatientImmunizationIncompleteReason(null))
    }
  }

  const setImmunizationType = (option: IOption, key: string) => {
    dispatch(setPatientImmunizationType(option, key))
  }

  const setImmunizationYear = (option: IOption, key: string) => {
    dispatch(setYear(option, key))
    dispatch(saveImmunization())
  }
  const setImmunizationIncompleteReason = (e: CustomEvent) => {
    e.preventDefault()
    dispatch(setPatientImmunizationIncompleteReason(e.detail.value))
  }

  const onAddImmunization = () => {
    dispatch(addPatientImmunization())
    const currentImmunization = immunizations[immunizations.length - 1]
    let content = document.getElementById(currentImmunization.key)
    setTimeout(() => {
      content?.scrollIntoView({
        behavior: 'smooth',
      })
      content = null
    }, 300)
  }

  const deleteImmunizationRecord = (key: string) => {
    dispatch(removePatientImmunization(key))
  }

  const isObjectEqual = (object: IPatientImmunization, fieldsToIgnore: string[]) => {
    const filteredCurrentState = filterOutObjectKeys(object, fieldsToIgnore)
    const filteredInitialState = filterOutObjectKeys(initialPatientImmunization(), fieldsToIgnore)
    return !compareObjectsIfEqual(filteredCurrentState, filteredInitialState)
  }

  const compareImmunizationState = () => {
    const patientImmunizationFieldsToIgnore = ['key']
    return immunizations.some((immunization) => {
      return isObjectEqual(immunization, patientImmunizationFieldsToIgnore)
    })
  }

  const showValidationError = (immune: IPatientImmunization) => {
    const patientImmunizationFieldsToIgnore = ['key', 'immunizationType']
    return isObjectEqual(immune, patientImmunizationFieldsToIgnore)
  }

  const ImmunizationContent = (
    <>
      <IonRow>
        <IonCol className='picklist'>
          <SingleSelect
            dataTestID='ImmunizationStatus'
            selectedOption={immunizationStatus}
            listHeader={t('immunization.status')}
            options={immunizationStatusOptions}
            action={setImmunizationStatus}
            label={t('immunization.status')}
          />
        </IonCol>
      </IonRow>
      {immunizationStatus?.id === -2 && (
        <>
          <hr className='field-divider' />
          <IonRow>
            <IonCol>
              <SInput
                label={t('immunization.incompleteReason')}
                dataTestID='IncompleteReason'
                inputValue={immunizationIncompleteReason || ''}
                onChangeAction={(e) => {
                  setImmunizationIncompleteReason(e)
                }}
              />
              <Validation id={PatientImmunizationsConstants.SET_PATIENT_IMMUNIZATIONS_INCOMPLETE_REASON} />
            </IonCol>
          </IonRow>
        </>
      )}
      <hr className='field-divider' />
      {immunizations.length > 0 &&
        immunizations.map((immunization: IPatientImmunization, index: number) => {
          return (
            <>
              <IonRow id={immunization.key}>
                <IonCol>
                  <IonItem lines='none'>
                    <div className='circle-container'>{index + 1}</div>
                  </IonItem>
                </IonCol>
                <IonCol>
                  <IonButtons className='ion-float-right '>
                    <IonButton
                      data-testid='Delete-Immunization'
                      onClick={() => deleteImmunizationRecord(immunization.key)}>
                      <IonIcon icon={trashOutline} />
                    </IonButton>
                  </IonButtons>
                </IonCol>
              </IonRow>
              <IonItemSliding key={immunization.key}>
                <IonGrid className='no-padding'>
                  <IonRow>
                    <IonCol className='picklist'>
                      <SingleSelect
                        dataTestID='ImmunizationType'
                        selectedOption={immunization.immunizationType}
                        listHeader={t('immunization.type')}
                        options={immunizationTypeOptions}
                        action={setImmunizationType}
                        label={t('immunization.type')}
                        dispatchData={immunization.key}
                      />
                      {showValidationError(immunization) && immunization.immunizationType === null && (
                        <IonItem lines='none' className='ion-no-margin validation'>
                          <IonLabel color='danger' className='ion-text-wrap ion-no-margin ion-no-padding'>
                            {t('immunization.immunizationTypeRequired')}
                          </IonLabel>
                        </IonItem>
                      )}
                    </IonCol>
                  </IonRow>
                  <hr className='field-divider' />
                  <IonRow>
                    <IonCol className='picklist'>
                      <SingleSelect
                        dataTestID='ImmunizationYear'
                        selectedOption={immunization.year}
                        listHeader={t('immunization.year')}
                        options={immunizationYearOptions}
                        action={setImmunizationYear}
                        label={t('immunization.year')}
                        dispatchData={immunization.key}
                      />
                    </IonCol>
                  </IonRow>
                  <hr className='field-divider' />
                </IonGrid>
              </IonItemSliding>
            </>
          )
        })}
      <IonRow>
        <IonCol>
          <IonButtons className='ion-float-right '>
            <IonButton
              data-testid='New'
              className='transform-none'
              fill='clear'
              color='primary-alt'
              onClick={() => onAddImmunization()}>
              <IonIcon icon={addCircleOutline} />
              <IonLabel>{t('immunization.new')}</IonLabel>
            </IonButton>
          </IonButtons>
        </IonCol>
      </IonRow>
    </>
  )

  return (
    <AccordianComponent
      dataTestId={'Immunization'}
      heading={'Immunizations'}
      content={ImmunizationContent}
      isCheckMark={compareImmunizationState() || !!immunizationStatus}
    />
  )
}

export default Immunization
